/**
 * Permission Utilities
 *
 * Functions for checking user permissions based on team role
 */

import { useAuthStore } from '@store/authStore';

export type TeamRole = 'Admin' | 'Regular' | 'ReadOnly';
export type ModuleId = 'dashboard' | 'people' | 'organisations' | 'volunteers' | 'assets' | 'reports' | 'finance';
export type ModuleAccess = 'edit' | 'read' | 'none';
export type ModulePermissions = Partial<Record<ModuleId, ModuleAccess>>;

/**
 * Permission levels in order of access
 * Admin > Regular > ReadOnly
 */
const ROLE_HIERARCHY: Record<TeamRole, number> = {
  Admin: 3,
  Regular: 2,
  ReadOnly: 1,
};

/**
 * Check if the current user has one of the allowed roles
 */
export function hasRole(allowedRoles: TeamRole[]): boolean {
  const { teamRole } = useAuthStore.getState();
  if (!teamRole) return false;
  return allowedRoles.includes(teamRole as TeamRole);
}

/**
 * Check if the current user is an Admin
 */
export function isAdmin(): boolean {
  const { teamRole } = useAuthStore.getState();
  return teamRole === 'Admin';
}

/**
 * Check if the current user can edit (Admin or Regular)
 */
export function canEdit(): boolean {
  return hasRole(['Admin', 'Regular']);
}

/**
 * Check if the current user is read-only
 */
export function isReadOnly(): boolean {
  const { teamRole } = useAuthStore.getState();
  return teamRole === 'ReadOnly';
}

/**
 * Check if a role has at least the minimum required access level
 */
export function hasMinimumRole(minimumRole: TeamRole): boolean {
  const { teamRole } = useAuthStore.getState();
  if (!teamRole) return false;

  const currentLevel = ROLE_HIERARCHY[teamRole as TeamRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[minimumRole];

  return currentLevel >= requiredLevel;
}

/**
 * Resolve the effective access level for a module.
 * Explicit modulePermissions override role-based defaults.
 * Dashboard can never be 'none' — minimum is 'read'.
 */
export function resolveModuleAccess(
  module: ModuleId,
  teamRole: TeamRole | null,
  modulePermissions?: ModulePermissions | null,
): ModuleAccess {
  const stored = modulePermissions?.[module];
  if (module === 'dashboard') {
    // Dashboard can never be 'none'
    if (stored && stored !== 'none') return stored;
    return teamRole === 'ReadOnly' ? 'read' : 'edit';
  }
  if (stored !== undefined) return stored;
  return teamRole === 'ReadOnly' ? 'read' : 'edit';
}

/**
 * Hook-friendly version that returns permission functions
 * Use this when you need reactive updates
 */
export function usePermissions() {
  const { teamRole } = useAuthStore();

  const currentRole = teamRole as TeamRole | null;

  return {
    role: currentRole,
    isAdmin: currentRole === 'Admin',
    canEdit: currentRole === 'Admin' || currentRole === 'Regular',
    isReadOnly: currentRole === 'ReadOnly',
    hasRole: (roles: TeamRole[]) => currentRole ? roles.includes(currentRole) : false,
    hasMinimumRole: (minRole: TeamRole) => {
      if (!currentRole) return false;
      const currentLevel = ROLE_HIERARCHY[currentRole] || 0;
      const requiredLevel = ROLE_HIERARCHY[minRole];
      return currentLevel >= requiredLevel;
    },
  };
}
